/**
 * Help — in-app FAQ + "how to" hub for authenticated users.
 *
 * Routes from /help (protected). Sections:
 *   • Hero with quick CTAs (open support chat, install app, contact)
 *   • FAQ accordion (~10 questions) — strictly Lumfi onboarding,
 *     billing, bots, FX, bonos and account topics. Editable later
 *     via Admin CMS once Iter-129+ ships.
 *   • "Still need help?" footer with WhatsApp / Telegram / email links.
 *
 * Localised via the LangContext (es/en/pt). Today only ES + EN strings
 * are populated — PT falls back to ES copy so the page never blanks.
 */
import { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { IconToggle } from "../components/ui/icon-toggle";
import { Input } from "../components/ui/input";
import { InstallAppButton } from "../components/pwa/InstallAppButton";
import { PageHeader } from "../components/PageHeader";
import { useLang } from "../contexts/LangContext";
import {
  HelpCircle, MessageCircle, Mail, Send, Smartphone,
  ChevronDown, ChevronUp, BookOpen, Sparkles, Download, Search, X,
} from "lucide-react";

const FAQS = {
  es: [
    {
      q: "¿Qué hace Lumfi exactamente?",
      a: "Lumfi calcula tu efectivo real disponible (no el saldo bancario simple), tu retiro semanal seguro y el nivel de riesgo del mes considerando cobros pendientes, gastos próximos, deudas y objetivos. Diseñado para freelancers e independientes con ingresos variables.",
    },
    {
      q: "¿Cómo empiezo? ¿Por dónde arranco?",
      a: "1) Completa el onboarding de 6 pasos (te toma 3-5 min). 2) Conecta tu primer movimiento desde 'Agregar' o el bot de WhatsApp/Telegram. 3) Revisa tu dashboard — ahí ves tu efectivo real y el retiro semanal sugerido.",
    },
    {
      q: "¿Cómo registro un cobro o pago recurrente?",
      a: "Ve a 'Cobros' o 'Pagos', toca '+ Agregar', llena monto y concepto, y activa 'Repetir' eligiendo la frecuencia (semanal/mensual/anual). Lumfi crea automáticamente cada instancia futura. Puedes editar la serie completa o sólo un evento.",
    },
    {
      q: "¿Cómo conecto el bot de WhatsApp o Telegram?",
      a: "Entra a 'Integraciones' en el menú lateral. Ahí ves los códigos QR y deep-links para vincular cada bot. Una vez vinculado, puedes escribir cosas como 'Pagué 500 al freelancer Juan' y Lumfi registra el movimiento automáticamente.",
    },
    {
      q: "¿Qué es un Bono y cómo lo recibo?",
      a: "Un Bono es un plan o addon premium que el equipo de Lumfi te asigna sin costo (campañas, beta access, compensación). Cuando recibes uno, aparece un badge violeta '🎁 BONO' al lado de 'Mi plan' en el menú. Vence automáticamente al final del período que el equipo configuró.",
    },
    {
      q: "¿Lumfi soporta varias monedas? ¿Cómo maneja el tipo de cambio?",
      a: "Sí. Configuras tu moneda principal en el perfil, pero puedes registrar movimientos en USD, ARS, MXN, COP, EUR y más. Lumfi convierte a tu moneda principal usando tasas FX actualizadas diariamente (con alerta cuando una tasa está stale).",
    },
    {
      q: "¿Cómo cancelo o cambio mi plan?",
      a: "Anda a 'Mi plan'. Ahí ves tu plan activo, próximos cargos y botones para upgrade/downgrade/cancelar. Las cancelaciones son inmediatas pero conservas acceso hasta que termine el período pago.",
    },
    {
      q: "¿Lumfi guarda mis datos bancarios?",
      a: "No. Lumfi NO se conecta a tu banco ni guarda credenciales. Todos los movimientos se registran manualmente o vía bots — tú controlas qué entra y qué no. Tus datos están encriptados en reposo y en tránsito.",
    },
    {
      q: "Reporté un bug y nadie me respondió, ¿qué hago?",
      a: "El chat de soporte (burbuja verde abajo a la derecha) llega directo al equipo. Si LumiBot no resuelve tu duda, un humano responde en horario hábil (lun-vie). Para urgencias, escríbenos desde el bot de Telegram con /soporte y un mensaje claro del problema.",
    },
    {
      q: "¿Puedo usar Lumfi desde el celular como una app nativa?",
      a: "Sí, es una PWA. Toca 'Instalar app' (abajo en la barra lateral o en esta página) y se agrega como ícono directo en tu pantalla de inicio. En iPhone usa Safari → Compartir → 'Agregar a inicio'.",
    },
  ],
  en: [
    {
      q: "What does Lumfi actually do?",
      a: "Lumfi calculates your real available cash (not the simple bank balance), your safe weekly withdrawal and the monthly risk level by considering pending receivables, upcoming payments, debts and goals. Built for freelancers with variable income.",
    },
    {
      q: "How do I get started?",
      a: "1) Complete the 6-step onboarding (3-5 minutes). 2) Log your first movement from 'Add' or the WhatsApp/Telegram bot. 3) Check your dashboard — you'll see your real cash and suggested weekly withdrawal.",
    },
    {
      q: "How do I register a recurring receivable or payment?",
      a: "Open 'Receivables' or 'Payments', tap '+ Add', fill amount and concept, then enable 'Repeat' and pick the frequency. Lumfi auto-creates every future instance. You can edit the whole series or a single event.",
    },
    {
      q: "How do I connect WhatsApp or Telegram bots?",
      a: "Go to 'Integrations' in the side menu. You'll see QR codes and deep-links to link each bot. Once linked, you can write things like 'Paid 500 to John' and Lumfi records it automatically.",
    },
    {
      q: "What is a Bonus and how do I get one?",
      a: "A Bonus is a premium plan or addon the Lumfi team grants free (campaigns, beta access, compensation). You'll see a violet '🎁 BONUS' badge next to 'My plan' in the menu. It auto-expires when the team-configured period ends.",
    },
    {
      q: "Does Lumfi support multiple currencies?",
      a: "Yes. Set your main currency in your profile but log movements in USD, ARS, MXN, COP, EUR and more. Lumfi converts using daily FX rates (with a warning when a rate is stale).",
    },
    {
      q: "How do I cancel or change my plan?",
      a: "Go to 'My plan'. You'll see your active plan, next charges and buttons to upgrade/downgrade/cancel. Cancellations are immediate but you keep access until the paid period ends.",
    },
    {
      q: "Does Lumfi store my banking credentials?",
      a: "No. Lumfi does NOT connect to your bank or store credentials. All movements are logged manually or via the bots — you control what's in. Your data is encrypted at rest and in transit.",
    },
    {
      q: "I reported a bug but nobody replied. What now?",
      a: "The support chat (green bubble bottom-right) goes straight to the team. If LumiBot can't resolve it, a human replies during business hours (Mon-Fri). For urgencies, message the Telegram bot with /soporte and a clear description.",
    },
    {
      q: "Can I use Lumfi from my phone as a native app?",
      a: "Yes, it's a PWA. Tap 'Install app' (in the sidebar footer or on this page) and it'll be added as a direct icon on your home screen. On iPhone use Safari → Share → 'Add to Home Screen'.",
    },
  ],
  pt: [
    {
      q: "O que o Lumfi faz exatamente?",
      a: "O Lumfi calcula seu caixa real disponível (não o saldo bancário simples), seu saque semanal seguro e o nível de risco do mês considerando recebíveis pendentes, pagamentos futuros, dívidas e metas. Feito para freelancers e autônomos com renda variável.",
    },
    {
      q: "Como começo? Por onde começar?",
      a: "1) Complete o onboarding de 6 passos (leva 3-5 min). 2) Registre seu primeiro movimento em 'Adicionar' ou pelo bot do WhatsApp/Telegram. 3) Confira seu dashboard — aí você vê seu caixa real e o saque semanal sugerido.",
    },
    {
      q: "Como registro um recebível ou pagamento recorrente?",
      a: "Vá em 'Recebíveis' ou 'Pagamentos', toque '+ Adicionar', preencha valor e descrição, e ative 'Repetir' escolhendo a frequência (semanal/mensal/anual). O Lumfi cria automaticamente cada instância futura. Você pode editar a série toda ou só um evento.",
    },
    {
      q: "Como conecto o bot do WhatsApp ou Telegram?",
      a: "Entre em 'Integrações' no menu lateral. Você verá os QR codes e deep-links para vincular cada bot. Uma vez vinculado, dá pra escrever 'Paguei 500 ao freelancer João' e o Lumfi registra o movimento sozinho.",
    },
    {
      q: "O que é um Bônus e como recebo um?",
      a: "Um Bônus é um plano ou add-on premium que a equipe do Lumfi te concede sem custo (campanhas, beta access, compensação). Quando você recebe um, aparece um badge violeta '🎁 BÔNUS' ao lado de 'Meu plano' no menu. Vence automaticamente no fim do período configurado pela equipe.",
    },
    {
      q: "O Lumfi suporta várias moedas? Como lida com o câmbio?",
      a: "Sim. Você configura sua moeda principal no perfil, mas pode registrar movimentos em USD, ARS, MXN, COP, BRL, EUR e mais. O Lumfi converte para sua moeda principal usando taxas de câmbio atualizadas diariamente (com alerta quando uma taxa fica desatualizada).",
    },
    {
      q: "Como cancelo ou troco meu plano?",
      a: "Vá em 'Meu plano'. Ali você vê seu plano ativo, próximas cobranças e botões para upgrade/downgrade/cancelar. Cancelamentos são imediatos mas você mantém o acesso até o fim do período pago.",
    },
    {
      q: "O Lumfi guarda meus dados bancários?",
      a: "Não. O Lumfi NÃO se conecta ao seu banco nem guarda credenciais. Todos os movimentos são registrados manualmente ou pelos bots — você controla o que entra e o que não entra. Seus dados ficam criptografados em repouso e em trânsito.",
    },
    {
      q: "Reportei um bug e ninguém respondeu, o que faço?",
      a: "O chat de suporte (bolha verde no canto inferior direito) chega direto na equipe. Se o LumiBot não resolver sua dúvida, um humano responde em horário comercial (seg-sex). Para urgências, escreva pelo bot do Telegram com /soporte e uma descrição clara do problema.",
    },
    {
      q: "Posso usar o Lumfi do celular como um app nativo?",
      a: "Sim, é um PWA. Toque em 'Instalar app' (no rodapé da barra lateral ou nesta página) e ele vai ser adicionado como ícone direto na sua tela inicial. No iPhone use Safari → Compartilhar → 'Adicionar à Tela de Início'.",
    },
  ],
};


function FaqItem({ q, a, idx }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-slate-200 last:border-b-0" data-testid={`faq-item-${idx}`}>
      <button /* design-system: ALLOW legacy-todo */
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`faq-toggle-${idx}`}
        className="w-full flex items-center justify-between gap-3 py-4 text-left hover:bg-slate-50/50 transition-colors px-1"
      >
        <span className="text-sm font-semibold text-slate-900">{q}</span>
        {open
          ? <ChevronUp className="h-4 w-4 text-slate-400 shrink-0" />
          : <ChevronDown className="h-4 w-4 text-slate-400 shrink-0" />}
      </button>
      {open && (
        <div className="pb-4 px-1 text-sm text-slate-700 leading-relaxed" data-testid={`faq-body-${idx}`}>
          {a}
        </div>
      )}
    </div>
  );
}


export default function Help() {
  const { lang } = useLang();
  const navigate = useNavigate();
  const isEs = lang === "es";
  const allFaqs = FAQS[lang] || FAQS.es;
  const [query, setQuery] = useState("");

  // iter-169zu · Fase 4 — Live FAQ filter. Case-insensitive match on
  // question + answer. Empty query returns the full list (zero re-renders
  // when the box is untouched). useMemo so the filter doesn't rerun on
  // every keystroke of unrelated state.
  const faqs = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allFaqs;
    return allFaqs.filter(
      (f) =>
        (f.q || "").toLowerCase().includes(q) ||
        (f.a || "").toLowerCase().includes(q),
    );
  }, [allFaqs, query]);

  const openSupportChat = () => {
    // Programmatically click the floating bubble — it lives in Layout
    // and is the single source of truth for support chat UX.
    const btn = document.querySelector('[data-testid="support-chat-bubble"]');
    if (btn) btn.click();
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="help-page">
        <PageHeader
          testid="help-header"
          eyebrow={isEs ? "Soporte" : "Support"}
          title={isEs ? "Centro de ayuda" : "Help center"}
          subtitle={isEs
            ? "Respuestas rápidas sobre cómo sacar el máximo a Lumfi."
            : "Quick answers on how to get the most out of Lumfi."}
          actions={
            <>
              <Button
                size="sm"
                onClick={openSupportChat}
                data-testid="help-open-chat-btn"
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                <MessageCircle className="h-3.5 w-3.5 mr-1.5" />
                {isEs ? "Abrir chat" : "Open chat"}
              </Button>
              <InstallAppButton
                variant="outline"
                size="sm"
                className="border-emerald-200 text-emerald-700 hover:bg-emerald-50"
              />
            </>
          }
        />

        {/* Hero */}
        <Card className="p-6 rounded-2xl bg-gradient-to-br from-emerald-50 via-white to-sky-50 border-emerald-200/60">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[260px]">
              <div className="flex items-center gap-2 mb-2">
                <HelpCircle className="h-5 w-5 text-emerald-600" />
                <p className="text-sm font-semibold text-emerald-700 uppercase tracking-wider">
                  {isEs ? "Cómo te ayudamos" : "How we help"}
                </p>
              </div>
              <p className="text-sm text-slate-600 max-w-2xl">
                {isEs
                  ? "Si no ves lo que buscas en las FAQ de abajo, escríbenos por el chat — LumiBot o el equipo te responden."
                  : "If you can't find what you're looking for in the FAQ below, chat us — LumiBot or the team will reply."}
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => navigate("/integrations")}
                  data-testid="help-open-integrations-btn"
                >
                  <BookOpen className="h-3.5 w-3.5 mr-1.5" />
                  {isEs ? "Conectar bots" : "Connect bots"}
                </Button>
              </div>
            </div>
            <div className="hidden sm:flex flex-col items-end gap-2 text-right">
              <div className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-sky-100 text-sky-700 text-[10px] font-bold uppercase tracking-wider">
                <Sparkles className="h-2.5 w-2.5" /> {isEs ? "LumiBot · IA disponible 24/7" : "LumiBot · AI 24/7"}
              </div>
              <p className="text-[10px] text-slate-400 max-w-[200px]">
                {isEs
                  ? "Respuestas instantáneas en el chat de soporte."
                  : "Instant replies on the support chat."}
              </p>
            </div>
          </div>
        </Card>

        {/* FAQ */}
        <Card className="p-4 sm:p-6 rounded-2xl border-slate-200" data-testid="help-faq">
          <h2 className="text-lg font-semibold text-slate-900 mb-2">
            {isEs ? "Preguntas frecuentes" : "Frequently asked questions"}
          </h2>
          <p className="text-xs text-slate-500 mb-3">
            {isEs ? `${faqs.length} de ${allFaqs.length} preguntas` : `${faqs.length} of ${allFaqs.length} questions`}
          </p>
          {/* iter-169zu · Sticky search filter (sticks to the FAQ card top
              so it stays reachable while the user scrolls long answers). */}
          <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 mb-2 bg-white/95 backdrop-blur border-b border-slate-100">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400 pointer-events-none" />
              <Input
                data-testid="help-faq-search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={isEs ? "Busca en las FAQs…" : "Search FAQs…"}
                className="h-11 pl-9 pr-9 text-base rounded-xl"
              />
              {query && (
                <IconToggle
                  onClick={() => setQuery("")}
                  data-testid="help-faq-search-clear"
                  aria-label={isEs ? "Limpiar búsqueda" : "Clear search"}
                  variant="ghost"
                  size="md"
                  className="absolute right-2 top-1/2 -translate-y-1/2"
                >
                  <X className="h-3 w-3" />
                </IconToggle>
              )}
            </div>
          </div>
          {faqs.length === 0 ? (
            <p className="text-sm text-slate-500 py-6 text-center" data-testid="help-faq-empty">
              {isEs ? "No encontré nada para esa búsqueda. Escribinos por el chat." : "Nothing matches that search. Chat us for help."}
            </p>
          ) : (
            <div className="divide-y divide-slate-200">
              {faqs.map((f, i) => (
                <FaqItem key={i} q={f.q} a={f.a} idx={i} />
              ))}
            </div>
          )}
        </Card>

        {/* Still need help */}
        <Card className="p-5 rounded-2xl border-slate-200" data-testid="help-contact">
          <h3 className="text-base font-semibold text-slate-900 mb-3">
            {isEs ? "¿Necesitas más ayuda?" : "Need more help?"}
          </h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <button /* design-system: ALLOW legacy-todo */
              type="button"
              onClick={openSupportChat}
              data-testid="help-contact-chat"
              className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50/40 transition-colors text-left"
            >
              <MessageCircle className="h-4 w-4 text-emerald-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-900">
                  {isEs ? "Chat en la app" : "In-app chat"}
                </p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {isEs ? "LumiBot responde al instante; humano en horario hábil." : "LumiBot replies instantly; humans in business hours."}
                </p>
              </div>
            </button>

            <a
              href="https://t.me/lumfi_bot"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="help-contact-telegram"
              className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 hover:border-sky-300 hover:bg-sky-50/40 transition-colors"
            >
              <Send className="h-4 w-4 text-sky-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-900">Telegram</p>
                <p className="text-[11px] text-slate-500 mt-0.5">
                  {isEs ? "Envía /soporte al bot." : "Send /soporte to the bot."}
                </p>
              </div>
            </a>

            <a
              href="mailto:soporte@lumfi.app"
              data-testid="help-contact-email"
              className="flex items-start gap-3 p-3 rounded-xl border border-slate-200 hover:border-sky-300 hover:bg-sky-50/40 transition-colors"
            >
              <Mail className="h-4 w-4 text-sky-600 mt-0.5 shrink-0" />
              <div>
                <p className="text-sm font-semibold text-slate-900">Email</p>
                <p className="text-[11px] text-slate-500 mt-0.5">soporte@lumfi.app</p>
              </div>
            </a>
          </div>
        </Card>

        {/* Mobile install hint */}
        <Card className="p-4 rounded-2xl border-dashed border-emerald-300 bg-emerald-50/30" data-testid="help-install-banner">
          <div className="flex items-start gap-3">
            <Smartphone className="h-5 w-5 text-emerald-600 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm font-semibold text-slate-900">
                {isEs ? "Instala Lumfi en tu celular" : "Install Lumfi on your phone"}
              </p>
              <p className="text-[12px] text-slate-600 mt-0.5">
                {isEs
                  ? "Acceso directo desde tu pantalla de inicio, sin pasar por el navegador. Toma 5 segundos."
                  : "Direct access from your home screen, no browser needed. 5 seconds."}
              </p>
            </div>
            <InstallAppButton
              variant="default"
              size="sm"
              className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
            />
          </div>
        </Card>
      </div>
    </Layout>
  );
}
